import React, { useEffect, useRef, useState } from 'react';
import { Card, Typography, Spin, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import Handsontable from 'handsontable';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.css';
import { formatMetricValue, getCurrencySymbol, formatPercentageValue } from '../utils/formatters';
import { getMetricColorStyle } from '../utils/colorUtils';

const { Title } = Typography;

// Description'a göre tüm metrik ID'lerini toplar
const createMetricMapping = (allMetricsData, brands, monthlyMetricsData = {}) => {
  // Bu metrik mapping'i şu yapıdadır:
  // {
  //   descriptions: { metricId: "description" }, // ID -> Description
  //   byDescription: { "description": [metricId1, metricId2, ...] }, // Description -> ID'ler
  //   info: { "description": { name, dataType, ... } } // Description -> Metrik Bilgisi
  // }
  
  const mapping = {
    descriptions: {},         // ID -> Description
    byDescription: {},        // Description -> ID'ler listesi
    info: {}                  // Description -> Metrik bilgisi
  };
  
  if (!brands) return mapping;
  
  // Tüm markaların metriklerini işle
  brands.forEach(brand => {
    let brandMetrics = [];
    
    // Önce allMetricsData'dan al
    if (allMetricsData && allMetricsData[brand.id]?.allMetrics) {
      brandMetrics = allMetricsData[brand.id].allMetrics;
    }
    // Eğer yoksa monthlyMetricsData'dan al
    else if (monthlyMetricsData && monthlyMetricsData[brand.id]?.metrics) {
      brandMetrics = monthlyMetricsData[brand.id].metrics;
    }
    
    if (!brandMetrics || brandMetrics.length === 0) return;
    
    brandMetrics
      // isVisible kontrolünü genişletiyoruz
      .filter(metric => {
        // İlk kontrol: direkt metrik üzerinde isVisible özelliği
        if (metric.isVisible === false) return false;
        
        // İkinci kontrol: BrandMetric ilişkisinde isVisible özelliği
        if (metric.BrandMetric && metric.BrandMetric.isVisible === false) return false;
        
        // Description kontrolü
        return metric.description && metric.description.trim() !== '';
      })
      .forEach(metric => {
        const description = metric.description?.trim();
        if (!description) return;
        
        // ID -> Description
        mapping.descriptions[metric.id] = description;
        
        // Description -> ID'ler
        if (!mapping.byDescription[description]) {
          mapping.byDescription[description] = [];
          
          // Metrik bilgisini de kaydet - description'ı ön plana çıkar
          mapping.info[description] = {
            description: description,  // Açıklamayı ön plana al
            name: metric.name || description,
            displayName: description,  // Görüntülenecek adı açıklama olarak belirle
            dataType: metric.dataType || 'numeric'
          };
        }
        
        if (!mapping.byDescription[description].includes(metric.id)) {
          mapping.byDescription[description].push(metric.id);
        }
      });
  });
  
  return mapping;
};

const SummaryTable = ({ 
  brands, 
  selectedMetrics,
  selectedMetricDescriptions = [],
  allMetricsData, 
  monthlyMetricsData = {},
  selectedMonth,
  onClose,
  mandatoryMetricDescriptions = [] // Zorunlu metrikler prop'u eklendi
}) => {
  const hotTableRef = useRef(null);
  const [tableData, setTableData] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metricMapping, setMetricMapping] = useState({
    descriptions: {},
    byDescription: {},
    info: {}
  });
  
  // Metrik bilgilerini topla
  useEffect(() => {
    const mapping = createMetricMapping(allMetricsData, brands, monthlyMetricsData);
    setMetricMapping(mapping);
  }, [brands, allMetricsData, monthlyMetricsData]);
  
  // Tablo verisi hazırlama
  useEffect(() => {
    if (!brands || brands.length === 0) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      // Kullanılacak description'ları belirle
      let descriptionsToShow = [];
      
      // Önce zorunlu metrikleri ekle
      if (mandatoryMetricDescriptions && mandatoryMetricDescriptions.length > 0) {
        descriptionsToShow = [...mandatoryMetricDescriptions];
      }
      
      // Eğer selectedMetricDescriptions varsa zorunlu olmayan ek metrikleri ekle
      if (selectedMetricDescriptions && selectedMetricDescriptions.length > 0) {
        const additionalMetrics = selectedMetricDescriptions.filter(desc => 
          !descriptionsToShow.includes(desc)
        );
        descriptionsToShow = [...descriptionsToShow, ...additionalMetrics];
      } 
      // Yoksa selectedMetrics ID'lerden description'ları bul ve ekle
      else if (selectedMetrics && selectedMetrics.length > 0) {
        selectedMetrics.forEach(metricId => {
          const description = metricMapping.descriptions[metricId];
          if (description && !descriptionsToShow.includes(description)) {
            descriptionsToShow.push(description);
          }
        });
      }
      
      // Eğer hiç metrik yoksa, sadece loading'i false yap
      if (descriptionsToShow.length === 0 && (!selectedMetrics || selectedMetrics.length === 0)) {
        setLoading(false);
        return;
      }
      
      // Sütunları hazırla
      const columns = [
        {
          data: 'brandName',
          type: 'text',
          readOnly: true,
          title: 'Marka'
        }
      ];
      
      // Her description için bir sütun ekle
      descriptionsToShow.forEach(description => {
        const metricInfo = metricMapping.info[description];
        if (metricInfo) {
          columns.push({
            data: `metric_${description}`,
            type: metricInfo.dataType === 'text' ? 'text' : 'numeric',
            numericFormat: metricInfo.dataType !== 'text' ? {
              pattern: '0,0.[00]',
              culture: 'tr-TR'
            } : undefined,
            readOnly: true,
            title: description,
            metricDescription: description
          });
        }
      });
      
      // Satırları hazırla - Her marka için bir satır
      const rows = brands.map(brand => {
        const row = {
          brandId: brand.id,
          brandName: brand.name
        };
        
        // Markanın metrik verilerini al - önce allMetricsData'dan, yoksa monthlyMetricsData'dan
        let brandMetrics = [];
        if (allMetricsData && allMetricsData[brand.id]?.allMetrics) {
          brandMetrics = allMetricsData[brand.id].allMetrics;
        } else if (monthlyMetricsData && monthlyMetricsData[brand.id]?.metrics) {
          brandMetrics = monthlyMetricsData[brand.id].metrics;
        }
        
        // Her description için değer bul
        descriptionsToShow.forEach(description => {
          // Bu description'a ait ID'leri bul
          const metricIds = metricMapping.byDescription[description] || [];
          
          // ÖNEMLİ DEĞİŞİKLİK: Her marka için o markanın isVisible=true olan metriklerini bul
          // Önce markanın kendi metriklerinden description'a uygun ve isVisible=true olanları bul
          const visibleMetrics = brandMetrics.filter(m => 
            m.isVisible !== false && // isVisible=true veya undefined (varsayılan true kabul edilir)
            m.description?.trim() === description && 
            metricIds.includes(m.id)
          );
          
          // İlk olarak görünür metrikleri kontrol et
          let metricData = null;
          
          if (visibleMetrics.length > 0) {
            // Görünür metrikler varsa, ilkini kullan
            metricData = visibleMetrics[0];
          } else {
            // Görünür metrik bulunamadıysa, tüm metrikler arasından ID eşleşenlerden ilkini kullan
            // Bu durumda metrik görünür olmasa bile en azından bir değer göstermiş oluruz
            metricData = brandMetrics.find(m => metricIds.includes(m.id));
          }
          
          if (metricData) {
            const metricInfo = metricMapping.info[description];
            let value = metricData.value;
            
            // Veri tipine göre formatla
            if (value !== null && value !== undefined) {
              if (metricInfo && metricInfo.dataType === 'currency') {
                value = Math.round(parseFloat(value));
              } else if (metricInfo && metricInfo.dataType === 'text' && value.length > 42) {
                value = value.substring(0, 42) + '...';
              }
            }
            
            row[`metric_${description}`] = value;
          } else {
            row[`metric_${description}`] = null;
          }
        });
        
        return row;
      });
      
      setTableColumns(columns);
      setTableData(rows);
    } catch (error) {
      console.error('Özet tablo oluşturma hatası:', error);
    } finally {
      setLoading(false);
    }
  }, [brands, selectedMetrics, selectedMetricDescriptions, mandatoryMetricDescriptions, metricMapping, allMetricsData, monthlyMetricsData, selectedMonth]);
  
  // Hücre render fonksiyonu
  const cellRenderer = (instance, td, row, col, prop, value, cellProperties) => {
    if (col === 0) {
      // Marka adı hücresi - özel stil
      Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
      td.style.fontWeight = 'bold';
      td.style.backgroundColor = '#f5f5f5';
      return td;
    }
    
    // Sütun nesnesini al
    const column = tableColumns[col];
    if (!column) return td;
    
    // Metrik açıklamasını al
    const metricDescription = column.metricDescription;
    if (!metricDescription) {
      Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
      return td;
    }
    
    // Marka ID'sini al
    const brandId = tableData[row]?.brandId;
    if (!brandId) {
      Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
      return td;
    }
    
    // Marka bilgilerini al - önce allMetricsData'dan, yoksa monthlyMetricsData'dan
    let brandInfo = {};
    if (allMetricsData && allMetricsData[brandId]?.brandInfo) {
      brandInfo = allMetricsData[brandId].brandInfo;
    } else if (monthlyMetricsData && monthlyMetricsData[brandId]) {
      // monthlyMetricsData'da brandInfo ayrı olarak gelmeyebilir, varsayılan değerler kullan
      brandInfo = { currency: 'TRY' };
    }
    const brandCurrency = brandInfo.currency || 'TRY';
    
    // Metrik bilgilerini al
    const metricInfo = metricMapping.info[metricDescription];
    const dataType = metricInfo?.dataType || 'numeric';
    
    // Değer null veya undefined ise boş göster
    if (value === null || value === undefined || value === '') {
      Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
      return td;
    }
    
    // Para birimi için özel render
    if (dataType === 'currency') {
      Handsontable.renderers.NumericRenderer(instance, td, row, col, prop, value, cellProperties);
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const currencySymbol = getCurrencySymbol(brandCurrency);
        td.textContent = Math.round(numValue).toLocaleString('tr-TR') + ' ' + currencySymbol;
      }
    } 
    // Yüzde için özel render
    else if (dataType === 'percentage') {
      Handsontable.renderers.NumericRenderer(instance, td, row, col, prop, value, cellProperties);
      td.textContent = formatPercentageValue(value);
    }
    // Metin için özel render
    else if (dataType === 'text') {
      Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
      
      if (String(value).length > 42) {
        td.title = value;
        td.textContent = String(value).substring(0, 42) + '...';
      }
    }
    // Diğer tipler için varsayılan render
    else {
      Handsontable.renderers.NumericRenderer(instance, td, row, col, prop, value, cellProperties);
    }
    
    // BrandCard'daki renklendirme mantığını uygula
    // Markanın tüm metriklerini al - önce allMetricsData'dan, yoksa monthlyMetricsData'dan
    let brandMetrics = [];
    if (allMetricsData && allMetricsData[brandId]?.allMetrics) {
      brandMetrics = allMetricsData[brandId].allMetrics;
    } else if (monthlyMetricsData && monthlyMetricsData[brandId]?.metrics) {
      brandMetrics = monthlyMetricsData[brandId].metrics;
    }
    
    // Mevcut sütunun metriğini bul - description üzerinden eşleştirme
    const currentMetric = brandMetrics.find(m => m.description?.trim() === metricDescription);
    
    // ROI metriği için debug (geçici)
    if (metricDescription && metricDescription.toLowerCase().includes('roi')) {
      console.log('ROI Debug:', {
        metricDescription,
        brandId,
        brandName: tableData[row]?.brandName,
        value,
        currentMetric,
        currentMetricId: currentMetric?.id,
        allMetricIds: brandMetrics.map(m => ({ id: m.id, description: m.description?.trim(), value: m.value }))
      });
    }
    
    if (currentMetric) {
      // 1. Önce colorUtils içindeki genel renklendirme fonksiyonunu kullan
      const colorStyle = getMetricColorStyle(currentMetric.id, value);
      
      if (colorStyle.backgroundColor) {
        td.style.backgroundColor = colorStyle.backgroundColor;
        td.style.color = colorStyle.color || '#000000';
        if (colorStyle.fontWeight) {
          td.style.fontWeight = colorStyle.fontWeight;
        }
      }
      
      // 2. Özel karşılaştırmalı metrikler için renklendirme (MonthlyMetrics ile aynı mantık)
      
      // 10009 ID'li metrik için 10008 ile karşılaştırma (tüm markalar için)
      if (currentMetric.id === 10009) {
        // Önce 10008'i dene
        let compareMetric = brandMetrics.find(m => m.id === 10008);
        
        // Eğer 10008 null ise, TARGET ROI description'ına sahip metriği bul
        if (!compareMetric || compareMetric.value === null || compareMetric.value === undefined) {
          compareMetric = brandMetrics.find(m => 
            m.description?.trim().toLowerCase().includes('target roi') && 
            m.value !== null && 
            m.value !== undefined
          );
          console.log('10008 null, TARGET ROI alternatifi arandı:', compareMetric);
        }
        
        console.log('10009 ROI karşılaştırması:', {
          brandName: tableData[row]?.brandName,
          currentMetric,
          compareMetric,
          compareMetricId: compareMetric?.id,
          currentValue: parseFloat(value),
          compareValue: compareMetric ? parseFloat(compareMetric.value) : 'bulunamadı',
          compareMetricValue: compareMetric?.value
        });
        
        if (compareMetric && compareMetric.value !== null && compareMetric.value !== undefined) {
          const currentValue = parseFloat(value);
          const compareValue = parseFloat(compareMetric.value);
          
          console.log('10009 değer analizi:', {
            brandName: tableData[row]?.brandName,
            currentValue,
            compareValue,
            isCurrentNaN: isNaN(currentValue),
            isCompareNaN: isNaN(compareValue)
          });
          
          if (!isNaN(currentValue) && !isNaN(compareValue)) {
            // ROI < Karşılaştırma metriği ise kırmızı, değilse yeşil
            td.style.backgroundColor = currentValue < compareValue ? 'rgba(107, 114, 128, 0.7)' : '#22c55e';
            td.style.color = currentValue < compareValue ? '#ffffff' : '#ffffff';
            td.style.fontWeight = 'bold';
            console.log('10009 renk uygulandı:', currentValue < compareValue ? 'GRİ' : 'YEŞİL', {
              currentValue,
              compareValue,
              backgroundColor: currentValue < compareValue ? 'rgba(107, 114, 128, 0.7)' : '#22c55e'
            });
          } else {
            console.log('10009 renk uygulanmadı - NaN değerler:', { currentValue, compareValue });
          }
        } else {
          console.log('10009 karşılaştırma metriği bulunamadı veya null:', compareMetric);
        }
      }
    } else if (metricDescription && metricDescription.toLowerCase().includes('roi')) {
      console.log('ROI metriği için currentMetric bulunamadı!', {
        metricDescription,
        brandId,
        availableDescriptions: brandMetrics.map(m => m.description?.trim())
      });
    }
    
    return td;
  };
  
  // TableSettings
  const tableSettings = {
    data: tableData,
    columns: tableColumns,
    rowHeaders: true,
    colHeaders: true,
    height: 'auto',
    width: '100%',
    licenseKey: 'non-commercial-and-evaluation',
    stretchH: 'all',
    autoColumnSize: true,
    contextMenu: false,
    manualColumnResize: true,
    readOnly: true,
    fixedColumnsLeft: 1,
    columnWidths: tableColumns.length > 0 ? [120, ...Array(Math.max(0, tableColumns.length - 1)).fill('auto')] : [120],
    cells: function() {
      const cellProps = {};
      cellProps.renderer = cellRenderer;
      return cellProps;
    }
  };

  return (
    <Card 
      title={<Title level={4}>Marka Metrikleri Özeti</Title>}
      style={{ marginTop: 16 }}
      extra={
        onClose && (
          <Button 
            type="text" 
            icon={<CloseOutlined />} 
            onClick={onClose}
            title="Özet tabloyu kapat"
          />
        )
      }
      styles={{ body: { padding: '0 12px 12px 12px' } }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Spin size="large" tip="Tablo yükleniyor..." />
        </div>
      ) : (selectedMetrics && selectedMetrics.length > 0) || (mandatoryMetricDescriptions && mandatoryMetricDescriptions.length > 0) ? (
        <div style={{ marginTop: 16, overflowX: 'auto' }}>
          <div style={{ minWidth: '600px' }}>
            <HotTable ref={hotTableRef} settings={tableSettings} />
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 20 }}>
          Lütfen gösterilecek metrikleri seçin.
        </div>
      )}
    </Card>
  );
};

export default SummaryTable; 